import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type { OnChangeValue } from 'react-select'
import { components } from 'react-select'
import { useTranslation } from 'react-i18next'

import ReactSelect from '@components/Form/ReactSelect'
import RoleChips from '@components/RoleChips'
import { IconChevronDown, IconPointFilled } from '@tabler/icons-react'

import NotInvitedCell from './NotInvitedCell'
import InvitedCell from './InvitedCell'

import { useHasPermission } from '@helpers/hooks/useHasPermission'
import { Perm } from '@type/permissions'
import { userRoleLabel } from '@src/constants'
import { zIndex } from '@src/zIndex'

import { useChangeUserRole } from '@queries/Teams'
import { useApproveJoinTeam } from '@queries/Account'
import useGetAccount from '@helpers/hooks/useGetAccount'
import { requestErrorsHandler } from '@helpers/utils'

import type { OrgStructureRecord } from '@pages/Team/Members/types'
import { Role as UserRole } from '@type/common'

export interface RoleProps {
  record: OrgStructureRecord
}

const enum NotMemberValues {
  invited = 'invited',
  requestingInvite = 'requestingInvite',
}

const optionObject: Record<string, Option> = {
  [UserRole.admin]: { label: userRoleLabel.Admin, value: UserRole.admin },
  [UserRole.requestor]: { label: userRoleLabel.Requestor, value: UserRole.requestor },
  [NotMemberValues.invited]: { label: userRoleLabel.invited, value: 'invited' },
  [NotMemberValues.requestingInvite]: { label: userRoleLabel.requestingInvite, value: 'requestingInvite' },
}

const userRoleOptions = [optionObject[UserRole.admin], optionObject[UserRole.requestor]]

const getRoleLabel = (user: OrgStructureRecord) => {
  if (user.auth_user_role) {
    return userRoleLabel[user.auth_user_role]
  }
  if (user.is_invited) {
    return userRoleLabel.invited
  }

  return userRoleLabel.notInvited
}

export const Role: React.FC<RoleProps> = ({ record }) => {
  const { t } = useTranslation(['common', 'org'])
  // Role chips become interactive (add/remove) when the user can
  // change other members' roles. Falls back to read-only chips
  // otherwise. The current-user-looking-at-themselves check below
  // applies regardless (you can't change your own roles).
  const canChangeRoles = useHasPermission(Perm.TEAM_MANAGE_MEMBER_ROLES)
  const user = useGetAccount()

  const { mutateAsync: changeRole } = useChangeUserRole(record.auth_user_id)
  const { mutateAsync: approveJoinTeam } = useApproveJoinTeam()

  const defaultValue = useMemo(() => {
    if (record.auth_user_role) {
      return optionObject[record.auth_user_role]
    }

    if (record.join_request) {
      return optionObject[NotMemberValues.requestingInvite]
    }

    return optionObject[NotMemberValues.invited]
  }, [record.auth_user_role, record.join_request])

  const [value, setValue] = useState<Option>(defaultValue)

  useEffect(() => {
    setValue(defaultValue)
  }, [defaultValue])

  const options = useMemo(() => {
    if (record.auth_user_role) {
      return userRoleOptions
    }
    return [optionObject.invited]
  }, [record.auth_user_role])

  const roleLabel = getRoleLabel(record)

  const handleChange = useCallback(
    async (option: OnChangeValue<Option, false>) => {
      if (!option) return
      // Optimistically show the picked role, but await the mutation so a
      // rejected change (403/validation/network) reverts the dropdown and
      // surfaces the error — without the await the rejection escaped the
      // try/catch and the UI showed a role the backend refused.
      const previous = value
      setValue(option as Option)
      try {
        await changeRole({ role_name: option.value })
      } catch (err) {
        setValue(previous)
        requestErrorsHandler(err)
      }
    },
    [value, changeRole],
  )

  const handleApproveChange = useCallback(
    async (option: OnChangeValue<Option, false>) => {
      try {
        if (option && record.join_request) {
          await approveJoinTeam({ id: record.join_request, role: option.value })
        }
      } catch (err) {
        requestErrorsHandler(err)
      }
    },
    [record.join_request, approveJoinTeam],
  )

  if (!canChangeRoles || user?.id === record.auth_user_id) {
    // Read-only view (non-admin OR the current user looking at their own row).
    // When the backend has populated role_assignments, render the chips
    // (no x, no add affordance — readOnly=true). Falls back to the
    // legacy single-label cell for users not yet migrated to RBAC.
    if (record.role_assignments && record.role_assignments.length > 0) {
      return (
        <div className="flex items-center gap-2 min-h-[38px] py-1">
          {!canChangeRoles ? <IconPointFilled className="w-3.5 h-3.5 -ml-1" /> : <div className="ml-[13px]" />}
          {/* Bounded so RoleChips' flex-wrap has something to wrap against.
              The table is min-w-max, so an unbounded cell sets its own width
              and a member with several roles drags the whole grid past the
              viewport instead of stacking the chips. */}
          <div className="max-w-[320px] min-w-0">
            <RoleChips teamUserId={record.id} assignedRoles={record.role_assignments} readOnly />
          </div>
        </div>
      )
    }
    return (
      <div className="flex items-center gap-2 min-h-[38px] py-1">
        {!canChangeRoles ? <IconPointFilled className="w-3.5 h-3.5 -ml-1" /> : <div className="ml-[13px]" />}
        {roleLabel}
      </div>
    )
  }

  if (record.join_request) {
    return (
      <div onClick={(e) => e.stopPropagation()} className="flex justify-start">
        <ReactSelect
          className="!mb-0 flex justify-center"
          options={[
            { label: t('common:userRoles.approveAsAdmin'), value: UserRole.admin },
            { label: t('common:userRoles.approveAsRequestor'), value: UserRole.requestor },
          ]}
          value={optionObject[NotMemberValues.requestingInvite]}
          onChange={handleApproveChange}
          menuPortalTarget={document.body}
          components={{
            Menu: ({ children, ...propsInput }) => (
              <components.Menu {...propsInput}>
                <div className="px-2 pt-2 font-bold text-grey_1">
                  {t('org:rolePicker.approveLabel', 'Approve access')}
                </div>
                {children}
              </components.Menu>
            ),
            SingleValue: ({ children, ...props }) => (
              <components.SingleValue {...props}>
                <span className="text-amber-700">{children}</span>
              </components.SingleValue>
            ),
            IndicatorsContainer: ({ children, ...props }) => (
              <components.IndicatorsContainer {...props}>
                <IconChevronDown stroke={1.25} className="w-5 h-5 mr-0.5 -ml-1 text-amber-700 cursor-pointer" />
                {children}
              </components.IndicatorsContainer>
            ),
          }}
          styles={{
            placeholder: (base) => ({ ...base, color: '#92400e' }),
            valueContainer: (base) => ({ ...base, padding: 0 }),
            menu: (base) => ({ ...base, width: '220px', borderRadius: '10px', overflow: 'hidden', paddingBottom: 8 }),
            menuPortal: (base) => ({ ...base, zIndex: zIndex.dropdown }),
            control: (base) => ({
              ...base,
              border: 'none',
              background: 'transparent',
              outline: 'none',
              boxShadow: 'none',
              flexDirection: 'row-reverse',
            }),
            indicatorSeparator: () => ({ display: 'none' }),
            dropdownIndicator: () => ({ display: 'none' }),
            input: (base) => ({ ...base, color: 'transparent' }),
          }}
        />
      </div>
    )
  }

  // No role yet, no pending join request, not yet invited:
  // open an invite Popup instead of hijacking react-select's Menu.
  if (!record.auth_user_role && !record.is_invited) {
    return <NotInvitedCell record={record} />
  }

  // Invite has been sent but not yet accepted. Render the badge +
  // Resend / Revoke action row (matches the modern share-dialog
  // pattern). Users with an active invite shouldn't be re-roled via
  // the legacy dropdown — they need to accept first, then admins
  // edit roles via the chip widget.
  if (!record.auth_user_role && record.is_invited) {
    return <InvitedCell record={record} />
  }

  // Multi-role rendering path: when the backend has populated
  // role_assignments (post-RBAC migration), render the chip widget
  // with add/remove affordances. Falls back to the legacy single-select
  // dropdown for users still on the auth.Group-only state.
  if (record.role_assignments && record.role_assignments.length > 0) {
    // The wrapper below is not an interactive element despite the handler:
    // it shields the row's own click from the chips inside it, same shape
    // as the join_request branch above. Adding role/tabIndex would announce
    // a control that performs nothing.
    return (
      <div onClick={(e) => e.stopPropagation()} className="flex justify-start max-w-[320px] min-w-0">
        <RoleChips teamUserId={record.id} assignedRoles={record.role_assignments} />
      </div>
    )
  }

  return (
    <div
      onClick={(e) => {
        e.stopPropagation()
      }}
      className="flex justify-start"
    >
      <ReactSelect
        className="!mb-0 flex justify-center"
        options={options}
        value={value}
        onChange={handleChange}
        menuPortalTarget={document.body}
        components={{
          Menu: ({ children, ...propsInput }) => (
            <components.Menu {...propsInput}>
              <div className="px-2 pt-2 font-bold text-grey_1">{t('org:rolePicker.label')}</div>
              {children}
            </components.Menu>
          ),
          Option: ({ children, ...props }) => {
            if (!record.auth_user_role) {
              return (
                <div className="px-2 flex flex-col" onClick={(e) => e.stopPropagation()}>
                  <span className="font-bold">{roleLabel}</span>
                  <span className="text-grey_1">{t('common:userRoles.description.invited')}</span>
                </div>
              )
            }
            return <components.Option {...props}>{children}</components.Option>
          },
          IndicatorsContainer: ({ children, ...props }) => {
            return (
              <components.IndicatorsContainer {...props}>
                <IconChevronDown stroke={1.25} className="w-5 h-5 mr-0.5 -ml-1 text-slate-950 cursor-pointer" />
                {children}
              </components.IndicatorsContainer>
            )
          },
        }}
        placeholder={roleLabel}
        styles={{
          placeholder: (base) => ({
            ...base,
            color: '#343A40',
          }),
          valueContainer: (base) => ({
            ...base,
            padding: 0,
          }),
          menu: (base) => ({
            ...base,
            width: '300px',
            borderRadius: '10px',
            overflow: 'hidden',
            paddingBottom: 8,
          }),
          menuPortal: (base) => ({
            ...base,
            zIndex: zIndex.dropdown,
          }),
          control: (base) => ({
            ...base,
            border: 'none',
            background: 'transparent',
            outline: 'none',
            boxShadow: 'none',
            flexDirection: 'row-reverse',
          }),
          indicatorSeparator: () => ({ display: 'none' }),
          dropdownIndicator: () => ({ display: 'none' }),
          input: (base) => ({
            ...base,
            color: 'transparent',
          }),
        }}
      />
    </div>
  )
}

export default React.memo(Role)
